import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import { useMutation } from '@tanstack/react-query';
import {
  postSubscribe,
  useGQL_teamSubscriptionStatus,
} from '@/contexts/graphql-and-swr-apis';
import { SimpleButton, PageContentWrapper } from '@unwrittenmedia/simple-components'

const cxStyles = {
  // Container & Layout
  container: 'mt-8 max-w-2xl mx-auto',
  cardWrapper: 'bg-white dark:bg-mist-900 shadow rounded-lg ring-1 ring-gray-200 dark:ring-mist-800 overflow-hidden',
  contentSection: 'px-4 py-5 sm:p-6',
  bottomSection: 'px-4 py-4 sm:px-6 bg-gray-50 dark:bg-mist-950/50',
  divider: 'border-t border-gray-200 dark:border-mist-800',

  // Typography - Labels & Text
  label: 'block text-sm font-medium text-gray-900 dark:text-white',
  labelWithSpacing: 'block text-sm font-medium text-gray-900 dark:text-white mb-2',
  secondaryText: 'text-sm text-gray-500 dark:text-mist-500',
  planTitle: 'text-sm font-semibold text-gray-900 dark:text-white',
  planPrice: 'text-2xl font-bold text-gray-900 dark:text-white',
  planInterval: 'text-base font-normal text-gray-500 dark:text-mist-500',

  // Form Inputs
  input: 'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-mist-700 placeholder:text-gray-400 dark:placeholder:text-mist-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm/6 bg-white dark:bg-mist-950',
  inputWrapper: 'mt-2',

  // Form Sections
  billingPlanSection: 'mt-6',
  planButton: 'w-full flex items-center justify-between px-4 py-3 border-2 border-gray-300 dark:border-mist-700 rounded-lg hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors',
  planInfo: 'flex flex-col items-start',

  // Error Styling
  errorContainer: 'mt-6 rounded-md bg-red-50 dark:bg-red-900/20 p-4',
  errorText: 'text-sm text-red-800 dark:text-red-400',
}

export const Route = createFileRoute('/venues/add-team')({
  component: AddTeamPage,
})

function PollingBox({ teamId }: { teamId: string }) {
  const { isSyncing, subscriptionInfo } = useGQL_teamSubscriptionStatus(teamId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSyncing) {
      navigate({ to: '/venues', search: { team: teamId } });
    }
  }, [isSyncing, teamId, navigate]);

  return (
    <div className="mt-6">
      <p className="text-sm text-gray-600 dark:text-mist-500 mb-2">
        {isSyncing ? 'Waiting for subscription to confirm…' : 'Confirmed! Redirecting…'}
      </p>
      <pre
        style={{ backgroundColor: 'purple', color: 'white' }}
        className="rounded-lg p-4 text-xs overflow-auto whitespace-pre-wrap"
      >
        {JSON.stringify(subscriptionInfo, null, 2) ?? '(polling…)'}
      </pre>
    </div>
  );
}

function AddTeamPage() {
  const { mutateAsync, isPending: isLoading, error } = useMutation({ mutationFn: postSubscribe });
  const navigate = useNavigate()
  const [teamName, setTeamName] = useState('')
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null)

  const handleSubscribe = async () => {
    try {
      const result = await mutateAsync({ teamName: teamName.trim(), planId: 'VALIDATEME' });
      setPendingTeamId(result.subscribe.id);
    } catch (_) {
      // error surfaced via error field
    }
  }

  return (
    <PageContentWrapper>
      <SimplePageHeader2
        title="Add Team"
        description="A team holds all of your screens, and controls who can manage them."
        actionButtonText="Create"
        actionButtonOnClick={handleSubscribe}
      />

      <div className={cxStyles.container}>
        <div className={cxStyles.cardWrapper}>
          <div className={cxStyles.contentSection}>
            {/* Team Name Input */}
            <div>
              <label
                htmlFor="team-name"
                className={cxStyles.label}
              >
                Team Name
              </label>
              <div className={cxStyles.inputWrapper}>
                <input
                  type="text"
                  name="team-name"
                  id="team-name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Enter team name"
                  className={cxStyles.input}
                />
              </div>
            </div>

            {/* Billing Plan Section */}
            <div className={cxStyles.billingPlanSection}>
              <label className={cxStyles.labelWithSpacing}>
                Billing Plan
              </label>
              <button
                type="button"
                className={cxStyles.planButton}
              >
                <div className={cxStyles.planInfo}>
                  <span className={cxStyles.planTitle}>
                    Prerelease Plan
                  </span>
                  <span className={cxStyles.secondaryText}>
                    Free, unrestricted account
                  </span>
                </div>
                <div className={cxStyles.planPrice}>
                  $0<span className={cxStyles.planInterval}>/mo</span>
                </div>
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className={cxStyles.errorContainer}>
                <div className={cxStyles.errorText}>
                  {error.message}
                </div>
              </div>
            )}

            {/* Polling box — shown after mutation returns a team ID */}
            {pendingTeamId && <PollingBox teamId={pendingTeamId} />}
          </div>

          {/* Horizontal Divider */}
          <div className={cxStyles.divider} />

          {/* Bottom Create Button */}
          <div className={cxStyles.bottomSection}>
            <SimpleButton
              onClick={handleSubscribe}
              disabled={isLoading || !!pendingTeamId || !teamName.trim()}
              className="w-full"
              color="primary"
              size="md"
            >
              {isLoading ? 'Creating…' : pendingTeamId ? 'Confirming…' : 'Create Team'}
            </SimpleButton>
          </div>
        </div>
      </div>
    </PageContentWrapper>
  )
}
